// Eltern-Portal: persönliche Kontaktdaten lesen/speichern.

import { getSupabaseClient } from "../supabase/index.js";
import { buildPostalAddressPersistPayload } from "../masterdata/postal-address.js";
import { deriveBirthYearFromBirthDate } from "./age-verification.js";
import { inferParentPortalAddressSameAsGuardian } from "./parent-portal-profile-address.js";
import { loadParentPortalProfileFromSsotRemote } from "./parent-portal-profile-ssot.js";

function __asStr(x) {
  return String(x ?? "").trim();
}

function __rpcFailureReason(error) {
  if (!error) return "";
  const code = __asStr(error?.code);
  if (code === "PGRST202") return "rpc-missing";
  return "rpc-error";
}

function __normalizePendingCriticalChange(raw) {
  const p = raw && typeof raw === "object" ? raw : null;
  if (!p) return null;
  const requestId = __asStr(p.requestId ?? p.request_id);
  if (!requestId) return null;
  return {
    requestId,
    birthDate: __asStr(p.birthDate ?? p.proposed_birth_date).slice(0, 10),
    birthYear: p.birthYear ?? p.proposed_birth_year ?? "",
    previousBirthDate: __asStr(
      p.previousBirthDate ?? p.previous_birth_date,
    ).slice(0, 10),
    previousBirthYear: p.previousBirthYear ?? p.previous_birth_year ?? "",
    requestedAt: p.requestedAt ?? p.requested_at ?? null,
    canApproveAsGuardian: Boolean(
      p.canApproveAsGuardian ?? p.can_approve_as_guardian,
    ),
  };
}

export function normalizeParentPortalProfileRow(raw) {
  const p = raw && typeof raw === "object" ? raw : {};
  return {
    id: __asStr(p.id),
    firstName: __asStr(p.firstName ?? p.first_name),
    lastName: __asStr(p.lastName ?? p.last_name),
    email: __asStr(p.email),
    phone: __asStr(p.phone),
    birthDate: __asStr(p.birthDate ?? p.birth_date).slice(0, 10),
    birthYear: p.birthYear ?? p.birth_year ?? "",
    street: __asStr(p.street),
    houseNumber: __asStr(p.houseNumber ?? p.house_number),
    postalCode: __asStr(p.postalCode ?? p.postal_code),
    city: __asStr(p.city),
    country: __asStr(p.country) || "DE",
    emailEditable: Boolean(p.emailEditable ?? p.email_editable) && false,
    isSelfPerson: Boolean(p.isSelfPerson ?? p.is_self_person),
    hasRegisteredGuardianRelation: Boolean(
      p.hasRegisteredGuardianRelation ?? p.has_registered_guardian_relation,
    ),
    criticalChangesRequireApproval: Boolean(
      p.criticalChangesRequireApproval ?? p.critical_changes_require_approval,
    ),
    pendingCriticalChange: __normalizePendingCriticalChange(
      p.pendingCriticalChange ?? p.pending_critical_change,
    ),
  };
}

/**
 * @param {string} personId
 */
export async function loadParentPortalPersonProfileRemote(personId) {
  const pid = __asStr(personId);
  if (!pid) return { ok: false, reason: "missing-person-id" };

  try {
    const ssot = await loadParentPortalProfileFromSsotRemote(pid);
    if (ssot?.ok) return ssot;

    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    const { data, error } = await supabase.rpc(
      "trainerstunden_parent_portal_get_person_profile",
      {
        p_person_id: pid,
      },
    );

    if (error) return { ok: false, reason: __rpcFailureReason(error), error };
    if (!data || typeof data !== "object")
      return { ok: false, reason: "no-data" };
    if (data.ok === false)
      return { ok: false, reason: __asStr(data.reason) || "unknown-error" };
    return { ok: true, profile: normalizeParentPortalProfileRow(data.profile) };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}

/**
 * @param {string} personId
 * @param {object} form
 */
export function buildParentPortalProfilePersistPayload(form) {
  const f = form && typeof form === "object" ? form : {};
  const birthDate = __asStr(f.birthDate ?? f.birth_date).slice(0, 10) || null;
  const birthYear = birthDate ? deriveBirthYearFromBirthDate(birthDate) : null;
  const postal = buildPostalAddressPersistPayload({
    street: f.street,
    house_number: f.houseNumber ?? f.house_number,
    postal_code: f.postalCode ?? f.postal_code,
    city: f.city,
    country: f.country || "DE",
  });

  return {
    first_name: __asStr(f.firstName ?? f.first_name) || null,
    last_name: __asStr(f.lastName ?? f.last_name) || null,
    phone: __asStr(f.phone) || null,
    birth_date: birthDate,
    birth_year: birthYear,
    street: postal.street,
    house_number: postal.house_number,
    postal_code: postal.postal_code,
    city: postal.city,
    country: postal.country,
  };
}

/**
 * @param {string} personId
 * @param {object} form
 */
export async function saveParentPortalPersonProfileRemote(personId, form) {
  const pid = __asStr(personId);
  if (!pid) return { ok: false, reason: "missing-person-id" };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    const payload = buildParentPortalProfilePersistPayload(form);
    const { data, error } = await supabase.rpc(
      "trainerstunden_parent_portal_update_person_profile",
      {
        p_person_id: pid,
        p_payload: payload,
      },
    );

    if (error) return { ok: false, reason: __rpcFailureReason(error), error };
    if (!data || typeof data !== "object")
      return { ok: false, reason: "no-data" };
    if (data.ok === false)
      return { ok: false, reason: __asStr(data.reason) || "unknown-error" };
    return {
      ok: true,
      profile: normalizeParentPortalProfileRow(data.profile),
      pendingCriticalApproval: Boolean(
        data.pendingCriticalApproval ?? data.pending_critical_approval,
      ),
    };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}

/**
 * @param {string} requestId
 */
export async function approveParentPortalProfileChangeRemote(requestId) {
  const rid = __asStr(requestId);
  if (!rid) return { ok: false, reason: "missing-request-id" };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    const { data, error } = await supabase.rpc(
      "trainerstunden_parent_portal_approve_profile_change",
      {
        p_request_id: rid,
      },
    );

    if (error) return { ok: false, reason: __rpcFailureReason(error), error };
    if (!data || typeof data !== "object")
      return { ok: false, reason: "no-data" };
    if (data.ok === false)
      return { ok: false, reason: __asStr(data.reason) || "unknown-error" };
    return { ok: true, profile: normalizeParentPortalProfileRow(data.profile) };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}

/**
 * @param {string} requestId
 */
export async function withdrawParentPortalProfileChangeRemote(requestId) {
  const rid = __asStr(requestId);
  if (!rid) return { ok: false, reason: "missing-request-id" };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    const { data, error } = await supabase.rpc(
      "trainerstunden_parent_portal_withdraw_profile_change",
      {
        p_request_id: rid,
      },
    );

    if (error) return { ok: false, reason: __rpcFailureReason(error), error };
    if (!data || typeof data !== "object")
      return { ok: false, reason: "no-data" };
    if (data.ok === false)
      return { ok: false, reason: __asStr(data.reason) || "unknown-error" };
    return { ok: true, profile: normalizeParentPortalProfileRow(data.profile) };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}

/**
 * @param {unknown} reason
 * @returns {string}
 */
export function resolveParentPortalProfileErrorKey(reason) {
  const r = __asStr(reason);
  switch (r) {
    case "person-not-allowed":
      return "trainerstunden.family.profile.errors.notAllowed";
    case "viewer-not-found":
      return "trainerstunden.family.profile.errors.viewerNotFound";
    case "person-not-found":
      return "trainerstunden.family.profile.errors.notFound";
    case "email-not-editable":
      return "trainerstunden.family.profile.errors.emailLocked";
    case "invalid-birth-date":
      return "trainerstunden.family.profile.errors.invalidBirthDate";
    case "invalid-birth-year":
      return "trainerstunden.family.profile.errors.invalidBirthYear";
    case "missing-person-id":
      return "trainerstunden.family.profile.errors.validation";
    case "missing-request-id":
      return "trainerstunden.family.profile.errors.validation";
    case "guardian-not-allowed":
      return "trainerstunden.family.profile.approval.errors.notAllowed";
    case "not-pending":
      return "trainerstunden.family.profile.approval.errors.notPending";
    case "request-not-found":
      return "trainerstunden.family.profile.approval.errors.notFound";
    case "not-allowed":
      return "trainerstunden.family.profile.approval.errors.withdrawNotAllowed";
    case "rpc-missing":
    case "rpc-error":
      return "trainerstunden.family.profile.errors.profileRpcUnavailable";
    default:
      return "trainerstunden.family.profile.errors.saveFailed";
  }
}

/**
 * @param {unknown} reason
 * @returns {string}
 */
export function resolveParentPortalProfileApprovalErrorKey(reason) {
  const r = __asStr(reason);
  switch (r) {
    case "guardian-not-allowed":
      return "trainerstunden.family.profile.approval.errors.notAllowed";
    case "not-pending":
      return "trainerstunden.family.profile.approval.errors.notPending";
    case "request-not-found":
      return "trainerstunden.family.profile.approval.errors.notFound";
    case "not-allowed":
      return "trainerstunden.family.profile.approval.errors.withdrawNotAllowed";
    case "missing-request-id":
      return "trainerstunden.family.profile.errors.validation";
    default:
      return "trainerstunden.family.profile.approval.errors.failed";
  }
}

/**
 * @param {object|null|undefined} profile
 * @param {object|null|undefined} child
 * @returns {object}
 */
export function mergeParentPortalProfileWithChild(profile, child) {
  const p = profile && typeof profile === "object" ? profile : {};
  const c = child && typeof child === "object" ? child : null;
  if (!c) return p;
  return {
    ...p,
    id: __asStr(p.id) || __asStr(c.id),
    firstName: __asStr(p.firstName) || __asStr(c.firstName ?? c.first_name),
    lastName: __asStr(p.lastName) || __asStr(c.lastName ?? c.last_name),
    birthDate:
      __asStr(p.birthDate ?? p.birth_date).slice(0, 10) ||
      __asStr(c.birthDate ?? c.birth_date).slice(0, 10),
  };
}

/**
 * @param {object|null|undefined} profile
 * @returns {object}
 */
export function parentPortalProfileToFormValues(profile, opts = {}) {
  const p = profile && typeof profile === "object" ? profile : {};
  const guardianAddress =
    opts.guardianAddress && typeof opts.guardianAddress === "object"
      ? opts.guardianAddress
      : null;
  const base = {
    firstName: __asStr(p.firstName),
    lastName: __asStr(p.lastName),
    email: __asStr(p.email),
    phone: __asStr(p.phone),
    birthDate: __asStr(p.birthDate).slice(0, 10),
    street: __asStr(p.street),
    houseNumber: __asStr(p.houseNumber ?? p.house_number),
    postalCode: __asStr(p.postalCode ?? p.postal_code),
    city: __asStr(p.city),
    country: __asStr(p.country) || "DE",
    addressSameAsGuardian: false,
  };
  if (opts.inferGuardianAddress && guardianAddress) {
    base.addressSameAsGuardian = inferParentPortalAddressSameAsGuardian(
      base,
      guardianAddress,
    );
  }
  return base;
}

/**
 * Wendet ausstehende kritische Werte auf Formular-Anzeige an (Vorschlag bis Freigabe).
 * @param {object} form
 * @param {object|null|undefined} profile
 */
export function applyPendingCriticalChangeToFormValues(form, profile) {
  const pending = profile?.pendingCriticalChange;
  if (!pending) return form;
  const next = { ...form };
  if (pending.birthDate)
    next.birthDate = __asStr(pending.birthDate).slice(0, 10);
  return next;
}

/**
 * @param {string} [personId]
 * @returns {string}
 */
export function buildParentPortalProfilePath(personId) {
  const base = "/trainingsdesk/family/kontaktdaten";
  const pid = __asStr(personId);
  if (!pid) return base;
  return `${base}?person=${encodeURIComponent(pid)}`;
}
